const API_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.token = localStorage.getItem('mkt_token') || null;
    this.refreshToken = localStorage.getItem('mkt_refresh_token') || null;
    this.companyId = localStorage.getItem('mkt_company_id') || null;
    this._refreshPromise = null;
  }

  setToken(token) {
    this.token = token;
    if (token) localStorage.setItem('mkt_token', token);
    else localStorage.removeItem('mkt_token');
  }

  setRefreshToken(token) {
    this.refreshToken = token;
    if (token) localStorage.setItem('mkt_refresh_token', token);
    else localStorage.removeItem('mkt_refresh_token');
  }

  setCompanyId(id) {
    this.companyId = id ? String(id) : null;
    if (id) localStorage.setItem('mkt_company_id', String(id));
    else localStorage.removeItem('mkt_company_id');
  }

  async _tryRefresh() {
    if (!this.refreshToken) return false;
    if (!this._refreshPromise) {
      this._refreshPromise = fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken })
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.token) {
            this.setToken(data.token);
            return true;
          }
          return false;
        })
        .catch(() => false)
        .finally(() => { this._refreshPromise = null; });
    }
    return this._refreshPromise;
  }

  async request(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (this.companyId) headers['X-Company-Id'] = this.companyId;

    const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/refresh');

    let response = await fetch(`${this.baseUrl}${endpoint}`, { ...options, headers });

    if (response.status === 401 && !options._isRetry && !isAuthEndpoint) {
      const refreshed = await this._tryRefresh();
      if (refreshed) {
        return this.request(endpoint, { ...options, _isRetry: true });
      }
      this.setToken(null);
      this.setRefreshToken(null);
    }

    const text = await response.text();
    let data;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }

    if (!response.ok) {
      const error = new Error(data.error || `Falha na requisicao (${response.status})`);
      error.status = response.status;
      error.details = data.details;
      throw error;
    }
    return data;
  }

  get(endpoint, params) {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request(endpoint + qs, { method: 'GET' });
  }

  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
  }

  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
  }

  del(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async upload(endpoint, file, fields = {}) {
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (this.companyId) headers['X-Company-Id'] = this.companyId;
    const form = new FormData();
    form.append('file', file);
    for (const [k, v] of Object.entries(fields)) form.append(k, v);
    const response = await fetch(`${this.baseUrl}${endpoint}`, { method: 'POST', headers, body: form });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.error || 'Falha no upload');
      error.status = response.status;
      throw error;
    }
    return data;
  }
}

const api = new ApiClient();
export default api;
